// src/api/chatApi.ts
import {
  ChatRequest,
  ChatResponseChunk,
  ChatStreamCallbacks,
  ChatResponseChunkType,
} from "../interfaces/chatTypes";
import { createLogger } from "../utils/logger";
import { constructRequest } from "./utils/apiHandler";
import { parseErrorResponse } from "./utils/errorHandler";
import { normalizeAccessToken } from "../utils/typeHelpers";
import { createClientErrorInfo } from "./utils/clientErrorHandler";
import { routeChunkToCallback, ChunkRouterState } from "./utils/chunkRouter";
import { streamErrorToPostHog, ApiNames } from "./utils/captureError";

const log = createLogger("ChatApi");

// Use environment variable for API base URL with fallback
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * Custom error class for chat API errors - follows the same pattern as ApiRequestError
 */
export class ChatApiError extends Error {
  errorCode: string;
  statusCode: number;
  requestId?: string;

  constructor(message: string, errorCode: string, statusCode: number, requestId?: string) {
    super(message);
    this.name = "ChatApiError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.requestId = requestId;
  }
}

/**
 * Enhanced Chat API communication class
 * Updated to handle the sophisticated response types from the new API schema
 * while maintaining backward compatibility with existing callback patterns
 */
export class ChatApi {
  private abortController: AbortController | null = null;
  private routerState: ChunkRouterState = {};

  /**
   * Send a chat request with enhanced streaming response handling
   * This method now supports the full range of response types from the updated API
   */
  async sendStreamingChatRequest(
    request: ChatRequest,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    // Log the request details to debug web search
    log.info("🔍 CHAT API REQUEST:", {
      hasDetails: !!request.details,
      webSearchFlag: request.details?.web_search,
      details: request.details,
    });

    try {
      // Notify that streaming has started
      callbacks.onStart();
      // Create abort controller for cancellation support
      this.abortController = new AbortController();
      // Reset router state for new streaming session
      this.routerState = {};

      // Construct form data with enhanced parameter handling
      const formData = this.buildFormData(request);

      // TEMPORARY DEBUG: Log the actual API call
      const endpoint = `${API_BASE_URL}/chat/redline_analyzer/stream/`;
      log.debug("📤 MAKING RED-LINE ANALYZER API CALL:", {
        method: "POST",
        endpoint: endpoint,
        formDataFields: (() => {
          const keys: string[] = [];
          formData.forEach((_, key) => keys.push(key));
          return keys;
        })(),
        hasFullFile: formData.has("full_file"),
        hasSelectedTextFile: formData.has("selected_text_file"),
        sessionId: formData.get("id"),
        access_token: request.access_token,
      });

      const api_request = constructRequest(
        endpoint,
        "POST",
        normalizeAccessToken(request.access_token),
        formData,
        this.abortController.signal
      );
      // Make the HTTP request to the chat/stream endpoint
      const response = await fetch(api_request);

      // Handle errors through the shared parser: it also runs the 401 refresh
      // bridge, so an expired token triggers a token refresh instead of every
      // subsequent request failing with the same stale token.
      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new ChatApiError(
          errorData.message,
          errorData.error_code,
          errorData.status_code,
          errorData.request_id
        );
      }

      // Process the enhanced streaming response
      await this.processStreamingResponse(response, callbacks);

      // Notify completion
      callbacks.onComplete();
    } catch (error) {
      // Handle different types of errors appropriately
      if (error instanceof DOMException && error.name === "AbortError") {
        // Request was cancelled - don't call error callback
        return;
      }

      if (error instanceof ChatApiError) {
        streamErrorToPostHog(error, ApiNames.CHAT_API, undefined, { chat_id: request.id });
        callbacks.onError(error);
      } else {
        // Handle client-side errors with helpful, contextual messages
        const clientError = createClientErrorInfo(error, "streaming");
        const apiError = new ChatApiError(clientError.message, clientError.errorCode, 0);
        // Also capture the ORIGINAL error: the wrapper only carries the
        // user-facing message and the CONNECTION_FAILED code, so
        // without this we never see the underlying message (e.g. "Failed to
        // fetch"). chat_id joins this to the BE "Stream outcome" log.
        streamErrorToPostHog(apiError, ApiNames.CHAT_API, undefined, {
          chat_id: request.id,
          original_error_name: error instanceof Error ? error.name : undefined,
          original_error_message: error instanceof Error ? error.message : String(error),
        });
        callbacks.onError(apiError);
      }
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Cancel an ongoing request - useful for user-initiated cancellations
   */
  cancelRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Build FormData object from request parameters
   * Enhanced to handle the new details configuration options
   */
  private buildFormData(request: ChatRequest): FormData {
    const formData = new FormData();

    // Required fields
    formData.append("message", request.message);
    formData.append("full_file", request.full_file);

    // Optional file
    if (request.selected_text_file) {
      formData.append("selected_text_file", request.selected_text_file);
    }

    // Additional files array (new)
    if (request.additional_files && request.additional_files.length > 0) {
      request.additional_files.forEach((file) => {
        formData.append("additional_files", file);
      });

      log.debug("Appending additional files", {
        fileCount: request.additional_files.length,
        fileNames: request.additional_files.map((f) => f.name),
      });
    }

    // Optional string parameters
    if (request.id) {
      formData.append("id", request.id);
    }

    if (request.doc_id) {
      formData.append("doc_id", request.doc_id);
    }

    if (request.bypass_cache !== undefined) {
      formData.append("bypass_cache", request.bypass_cache.toString());
    }

    // Enhanced details handling - ensure new API fields are properly formatted
    if (request.details) {
      // Log the details being sent for debugging the new features
      log.debug("Sending enhanced details configuration", {
        web_search: request.details.web_search,
        thinking: request.details.thinking,
        hasLegacyFields: !!(request.details.provider || request.details.temperature),
      });

      formData.append("details", JSON.stringify(request.details));
    }

    // Enhanced chat history handling
    if (request.chat_history && request.chat_history.length > 0) {
      log.debug("Sending conversation history", {
        messageCount: request.chat_history.length,
        hasThinkingContent: request.chat_history.some(
          (msg) => msg.role === "assistant" && !Array.isArray(msg.content) && msg.content.thinking
        ),
      });

      formData.append("chat_history", JSON.stringify(request.chat_history));
    }

    return formData;
  }

  /**
   * Process enhanced streaming Server-Sent Events response
   * Updated to handle the sophisticated response format from the new API
   */
  private async processStreamingResponse(
    response: Response,
    callbacks: ChatStreamCallbacks
  ): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Could not get response reader for streaming");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Accumulate chunks into buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines from buffer
        buffer = this.processBufferedLines(buffer, callbacks);
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Process complete lines from the streaming buffer
   * Enhanced to handle the new response format while maintaining robustness
   */
  private processBufferedLines(buffer: string, callbacks: ChatStreamCallbacks): string {
    const lines = buffer.split("\n");

    // Keep the last potentially incomplete line in the buffer
    const remainingBuffer = lines.pop() || "";

    // Process all complete lines
    for (const line of lines) {
      const trimmedLine = line.trim();

      // Skip empty lines
      if (!trimmedLine) {
        continue;
      }

      // Process data lines (SSE format: "data: {json}")
      if (trimmedLine.startsWith("data:")) {
        const jsonData = trimmedLine.substring(5).trim(); // Remove "data:" prefix

        // Handle end-of-stream marker
        if (jsonData === "[DONE]") {
          break;
        }

        try {
          const chunk: ChatResponseChunk = JSON.parse(jsonData);

          // DEBUG: Log raw SSE data for tool_use_result to diagnose playbook structure
          if (chunk.type === "tool_use_result") {
            log.info("🔬 RAW SSE TOOL_USE_RESULT - BEFORE ROUTING", {
              rawJsonDataPreview: jsonData.substring(0, 500),
              parsedChunkType: chunk.type,
              parsedChunkTextType: typeof chunk.text,
              parsedChunkTextKeys:
                typeof chunk.text === "object" && chunk.text !== null
                  ? Object.keys(chunk.text)
                  : "N/A",
              // Check for nested structure indicators
              hasToolName:
                typeof chunk.text === "object" && chunk.text !== null && "tool_name" in chunk.text,
              hasReason:
                typeof chunk.text === "object" && chunk.text !== null && "reason" in chunk.text,
              hasContent:
                typeof chunk.text === "object" && chunk.text !== null && "content" in chunk.text,
              // Check for flat structure indicators
              hasPlaybook:
                typeof chunk.text === "object" && chunk.text !== null && "playbook" in chunk.text,
              hasSections:
                typeof chunk.text === "object" && chunk.text !== null && "sections" in chunk.text,
            });
          }

          this.routeChunkToCallback(chunk, callbacks);
        } catch (error) {
          log.error("Failed to parse streaming chunk", {
            jsonData: jsonData.substring(0, 200) + (jsonData.length > 200 ? "..." : ""),
            error: error instanceof Error ? error.message : String(error),
            chunkLength: jsonData.length,
          });
          // Continue processing other chunks even if one fails
        }
      }
    }

    return remainingBuffer;
  }

  /**
   * Enhanced chunk routing to handle all response types
   * This method implements a sophisticated routing system that maintains backward
   * compatibility while supporting all the new API features
   */
  private routeChunkToCallback(chunk: ChatResponseChunk, callbacks: ChatStreamCallbacks): void {
    const chunkType = chunk.type as ChatResponseChunkType;

    // Log chunk processing for debugging the new features
    // Special logging for web search related events
    if (
      chunkType === "server_tool_use" ||
      chunkType === "input_json_delta" ||
      chunkType === "web_search_tool_result"
    ) {
      log.info(`${chunkType}`, {
        type: chunkType,
        textContent: chunk.text,
        textType: typeof chunk.text,
      });
    } else {
      log.debug("Processing response chunk", {
        type: chunkType,
        hasTextContent: typeof chunk.text === "string" && chunk.text.length > 0,
        hasObjectContent: typeof chunk.text === "object",
        textPreview:
          typeof chunk.text === "string"
            ? chunk.text.substring(0, 50) + (chunk.text.length > 50 ? "..." : "")
            : "[object]",
      });
    }

    // Use shared chunk router with chatApi-specific configuration and persistent state
    routeChunkToCallback(chunk, callbacks, log, {
      verboseAutoRedlineLogging: true,
      verboseWebSearchLogging: true,
      enhancedErrorHandling: true,
      enableFallbackToText: true,
      state: this.routerState,
    });
  }
}

/**
 * Utility function to create File objects from OOXML strings
 * This bridges the gap between your existing string-based document handling
 * and the FastAPI's file-based approach
 */
export function createXmlFile(content: string, filename: string): File {
  const blob = new Blob([content], { type: "application/xml" });
  return new File([blob], filename, { type: "application/xml" });
}

/**
 * Generate a unique document ID for caching purposes
 * This creates a consistent ID based on document content hash
 */
export function generateDocumentId(ooxmlContent: string): string {
  // Simple hash function - in production we might want a more robust hash
  let hash = 0;
  for (let i = 0; i < ooxmlContent.length; i++) {
    const char = ooxmlContent.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `doc_${Math.abs(hash).toString(36)}`;
}

/**
 * Create and export a singleton instance - follows the pattern of your other API files
 */
export const chatApi = new ChatApi();
